/**
 * pendingDocuments — o card "Pendências" da home, agrupado por TIPO.
 *
 * ─── O QUE ISTO SUBSTITUIU, E POR QUÊ ──────────────────────────────────────
 *
 * A home tinha um alerta que contava UMA coisa: orçamentos pendentes há mais de
 * 3 dias. Era o item mais acionável da tela e, ao mesmo tempo, o mais estreito:
 * uma OS que nunca foi concluída, um contrato mandado e não assinado e uma
 * conta vencida não apareciam em lugar nenhum — o prestador só descobria
 * abrindo módulo por módulo. Este módulo generaliza a pergunta ("o que está em
 * aberto?") mantendo a resposta ORGANIZADA: uma linha por tipo de documento,
 * nunca uma lista de documentos (que cresceria sem teto e viraria uma segunda
 * lista de "Últimos documentos" logo abaixo da primeira).
 *
 * ─── AS REGRAS QUE QUEBRAM EM SILÊNCIO ─────────────────────────────────────
 *
 * 1. **O predicado é POSITIVO, sempre.** `document_type` é NULLABLE e, em SQL,
 *    `<> 'pedido'` para uma linha sem tipo é NULL, não `true` (#82-b). A venda
 *    de balcão sai por `isSaleRow`, e o orçamento LEGADO sem tipo continua
 *    sendo orçamento. O mesmo vale para os status: listas explícitas do que É
 *    pendente, nunca "tudo menos X" — assim uma linha com status desconhecido
 *    não entra na conta por acidente.
 *
 * 2. **VENDA DE BALCÃO NUNCA É PENDÊNCIA.** Ela nasce `completed` (`quickSale`),
 *    então hoje já sairia pelo status — mas o filtro por tipo fica explícito
 *    para o dia em que isso mudar não ressuscitar o #82.
 *
 * 3. **`null` (não posso ler) ≠ `[]` (posso ler e não há nada).** Vale para
 *    `financeEntries` (RLS owner/admin) e para `nfseDocuments` (módulo
 *    online-only, sem cache offline). Grupo cuja origem é `null` simplesmente
 *    NÃO APARECE — mostrar "0 contas em aberto" a quem não pode lê-las é a
 *    mentira do #38, e mostrar a NFS-e zerada offline seria informação errada.
 *
 * 4. **ROTA É DADO, não string montada na tela.** As rotas do app são bilíngues
 *    (`/quotes` em inglês, `/ordens-servico` em português) e uma rota inventada
 *    por analogia compila, não gera warning e cai no catch-all mostrando 404 —
 *    bug que já viveu em produção (#56). Cada grupo devolve a sua, e há teste
 *    afirmando as cinco contra o `App.tsx`.
 *
 * 5. **Só o orçamento ganha filtro na URL, e isso é uma consequência do
 *    vocabulário.** "Pendente" é exatamente UMA aba em `/quotes`; em
 *    `/ordens-servico` ele abrange duas (`pending` + `in_progress`), então não
 *    existe aba única para apontar e o destino é a raiz do módulo. Deep link
 *    para um recorte que a tela de destino não sabe reproduzir mostraria uma
 *    contagem diferente da que o card prometeu.
 *
 * O RECIBO fica de fora: ele não tem estado pendente (emitido ou cancelado —
 * `receipts` não tem coluna `status`, #57). O COMPROMISSO da agenda também: não
 * é documento gerado, é marcação (mesma régua de `recentDocuments.ts`).
 */
import { computeFinanceStatus } from '@/lib/financeStatus';
import { isSaleRow } from '@/lib/clientSummary';
import { NFSE_STATUS_META } from '@/types/fiscal';
import type { FinanceEntryWithStatus } from '@/types/database';

// ===========================================================================
// Vocabulários de "pendente"
// ===========================================================================

/** Orçamento esperando resposta do cliente. */
export const PENDING_QUOTE_STATUSES = ['pending'] as const;

/** OS aberta ou em execução — só `completed`/`cancelled` encerram. */
export const PENDING_SERVICE_ORDER_STATUSES = ['pending', 'in_progress'] as const;

/** Contrato que ainda não colheu todas as assinaturas. */
export const PENDING_CONTRACT_STATUSES = ['draft', 'sent', 'partially_signed'] as const;

/**
 * Os dois desfechos ASSENTADOS da NFS-e. Tudo o mais pede ação do prestador —
 * inclusive `rejected` (corrigir e reemitir) e `unknown_reconciliation_required`
 * (reconciliar com o Sefin), que são justamente os que não podem ficar
 * invisíveis.
 */
const SETTLED_NFSE_STATUSES = new Set(['authorized', 'cancelled']);

/**
 * Derivado de `NFSE_STATUS_META` de propósito: uma lista escrita à mão aqui
 * envelheceria em silêncio no dia em que a máquina de estados fiscal ganhasse
 * um status novo — e o efeito seria uma nota travada sumindo do card.
 */
export const PENDING_NFSE_STATUSES: string[] = Object.keys(NFSE_STATUS_META).filter(
  (status) => !SETTLED_NFSE_STATUSES.has(status),
);

// ===========================================================================
// Tipos
// ===========================================================================

export type PendingKind = 'quote' | 'service_order' | 'contract' | 'nfse' | 'finance';

export interface PendingGroup {
  kind: PendingKind;
  /** Rótulo AUTOEXPLICATIVO: a linha é única, não há subtítulo para socorrê-la. */
  label: string;
  count: number;
  /**
   * Quantos dos `count` já passaram do prazo (orçamento parado há mais de 3
   * dias, conta vencida). `0` quando o tipo não tem noção de atraso.
   */
  overdueCount: number;
  /** Rota interna — ver a regra 4 no cabeçalho. */
  route: string;
}

export interface PendingDocumentsSources {
  /** Orçamentos JÁ carregados pela home (inclui vendas de balcão, filtradas aqui). */
  quotes?: any[] | null;
  serviceOrders?: any[] | null;
  contracts?: any[] | null;
  /** `null` = sem rede (o módulo fiscal não tem cache) ou fora de alcance. */
  nfseDocuments?: any[] | null;
  /** `null` = o papel do usuário não pode ler o financeiro (#38). */
  financeEntries?: FinanceEntryWithStatus[] | null;
}

export interface PendingSummary {
  groups: PendingGroup[];
  /** Soma dos `count` — o número do cabeçalho do card. */
  total: number;
}

/**
 * A partir de quantos dias sem resposta um orçamento vira cobrança.
 *
 * Herdado do alerta que este card substituiu: 3 dias é o prazo em que o
 * prestador ainda lembra do serviço e o cliente ainda lembra do pedido.
 */
export const STALE_QUOTE_DAYS = 3;

const DAY_MS = 24 * 60 * 60 * 1000;

// ===========================================================================
// Agregação
// ===========================================================================

function hasStatus(row: any, allowed: readonly string[] | string[]): boolean {
  return allowed.includes(String(row?.status ?? ''));
}

/**
 * As pendências agrupadas por tipo, na ordem do ciclo comercial (orçamento →
 * contrato → OS → dinheiro → nota). Grupos com contagem ZERO são omitidos: o
 * card existe para dizer o que falta fazer, e uma linha "0 contratos" só ocupa
 * a tela do prestador que não usa contrato.
 *
 * @param now injetável para o teste não depender do relógio (mesma convenção de
 *   `computeFinanceStatus`) — ver #154.
 */
export function buildPendingSummary(
  sources: PendingDocumentsSources,
  now: Date = new Date(),
): PendingSummary {
  const groups: PendingGroup[] = [];

  // --- Orçamentos -----------------------------------------------------------
  // Regra 2: a venda de balcão sai pelo TIPO, não pelo status.
  const quotes = (sources.quotes ?? []).filter(
    (q) => q && !isSaleRow(q) && hasStatus(q, PENDING_QUOTE_STATUSES),
  );
  if (quotes.length > 0) {
    const staleBefore = now.getTime() - STALE_QUOTE_DAYS * DAY_MS;
    const stale = quotes.filter((q) => {
      const at = new Date(String(q.created_at ?? '')).getTime();
      return Number.isFinite(at) && at < staleBefore;
    }).length;
    groups.push({
      kind: 'quote',
      label: 'Orçamentos sem resposta',
      count: quotes.length,
      overdueCount: stale,
      // Regra 5: "pendente" é exatamente uma aba em /quotes.
      route: '/quotes?status=pending',
    });
  }

  // --- Contratos ------------------------------------------------------------
  const contracts = (sources.contracts ?? []).filter(
    (c) => c && hasStatus(c, PENDING_CONTRACT_STATUSES),
  );
  if (contracts.length > 0) {
    groups.push({
      kind: 'contract',
      label: 'Contratos não assinados',
      count: contracts.length,
      overdueCount: 0,
      route: '/contratos',
    });
  }

  // --- Ordens de serviço ----------------------------------------------------
  const orders = (sources.serviceOrders ?? []).filter(
    (o) => o && hasStatus(o, PENDING_SERVICE_ORDER_STATUSES),
  );
  if (orders.length > 0) {
    groups.push({
      kind: 'service_order',
      label: 'Ordens de serviço abertas',
      count: orders.length,
      overdueCount: 0,
      route: '/ordens-servico',
    });
  }

  // --- Financeiro (pode estar fora de alcance por papel) ---------------------
  // Regra 3: `null` omite o grupo; `[]` conta zero e também o omite.
  if (sources.financeEntries) {
    let open = 0;
    let overdue = 0;
    for (const entry of sources.financeEntries) {
      if (!entry) continue;
      // `computed_status` vem da VIEW; o espelho local só entra quando a linha
      // veio do cache offline sem a coluna computada.
      const status =
        entry.computed_status ?? computeFinanceStatus(entry.status, entry.due_date);
      if (status === 'pending') open += 1;
      else if (status === 'overdue') {
        open += 1;
        overdue += 1;
      }
    }
    if (open > 0) {
      groups.push({
        kind: 'finance',
        label: 'Contas em aberto',
        count: open,
        overdueCount: overdue,
        route: '/financeiro',
      });
    }
  }

  // --- NFS-e ----------------------------------------------------------------
  if (sources.nfseDocuments) {
    const notes = sources.nfseDocuments.filter((n) => n && hasStatus(n, PENDING_NFSE_STATUSES));
    if (notes.length > 0) {
      groups.push({
        kind: 'nfse',
        label: 'Notas fiscais pendentes',
        count: notes.length,
        overdueCount: 0,
        route: '/nfse',
      });
    }
  }

  return { groups, total: groups.reduce((sum, g) => sum + g.count, 0) };
}
